import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, Loader2, Trophy, Users } from "lucide-react";
import { toast } from "sonner";
import { Main } from "@/components/Main";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getClientResults, ClientResultsResponse } from "@/Api/clientResults";
import { useAuth } from "@/hooks/auth";
import { Mask } from "@/utils/mask";
import {
  ClientResultsFilterState,
  FiltersBar,
  LotteryChip,
  defaultFilters,
  filtersFromSearch,
  filtersToSearch,
  toParams,
} from "./shared";

/**
 * Vendas > Resultado Clientes.
 * Clientes (Endosso) que compraram nos concursos do filtro, inclusive quem não ganhou.
 * Vendedor vê só as próprias vendas (regra aplicada também na API).
 */
export const PageList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auth = useAuth();
  const isSeller = !!auth?.user?.seller?.id;

  const [filters, setFilters] = useState<ClientResultsFilterState>(() => filtersFromSearch(searchParams));
  const [data, setData] = useState<ClientResultsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async (f: ClientResultsFilterState, page = 1) => {
    if (f.date_start && f.date_end && f.date_start > f.date_end) {
      toast.error("A data final deve ser igual ou posterior à data inicial.");
      return;
    }
    setLoading(true);
    try {
      const { data: res, ok } = await getClientResults({ ...toParams(f), page });
      if (ok && res?.response) {
        setData(res.response);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(filters, Number(searchParams.get("page")) || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apply = (f: ClientResultsFilterState, page = 1) => {
    const search = new URLSearchParams(filtersToSearch(f));
    if (page > 1) search.set("page", String(page));
    setSearchParams(search, { replace: true });
    fetchData(f, page);
  };

  const handleClear = () => {
    const f = defaultFilters();
    setFilters(f);
    apply(f);
  };

  const openClient = (uuid: string) => {
    navigate(`/resultado-clientes/${uuid}?${filtersToSearch(filters)}`);
  };

  const rows = data?.data ?? [];

  return (
    <Main>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
          AP
        </div>
        <div>
          <h2 className="text-2xl font-semibold leading-tight">Apuração por cliente</h2>
          <p className="text-sm text-muted-foreground">Vendas / Resultado Clientes</p>
        </div>
      </div>

      <FiltersBar
        value={filters}
        onChange={setFilters}
        onApply={() => apply(filters)}
        onClear={handleClear}
        showSellerFilters={!isSeller}
        loading={loading}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card>
          <CardContent className="flex items-center justify-between p-5">
            <div>
              <p className="text-sm text-muted-foreground">Clientes no período</p>
              <p className="text-2xl font-bold tabular-nums">{data?.totals.total_clients ?? 0}</p>
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-900/30">
              <Users className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center justify-between p-5">
            <div>
              <p className="text-sm text-muted-foreground">Total em prêmios</p>
              <p className="text-2xl font-bold tabular-nums text-green-700 dark:text-green-400">
                {Mask.formatCurrency(data?.totals.total_prize ?? 0)}
              </p>
              {!!data?.totals.pending_contests_count && (
                <p className="text-xs text-muted-foreground">
                  {data.totals.pending_contests_count}{" "}
                  {data.totals.pending_contests_count === 1 ? "concurso não apurado" : "concursos não apurados"} fora
                  do total
                </p>
              )}
            </div>
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900/30">
              <Trophy className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome do cliente</TableHead>
                <TableHead>Loterias e concursos</TableHead>
                <TableHead className="text-right">Total em prêmios</TableHead>
                <TableHead className="text-right">Saldo no sistema</TableHead>
                <TableHead className="w-16 text-center">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                    Nenhum cliente com compra Endosso nos concursos deste filtro.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.client.id}>
                    <TableCell className="font-medium">{row.client.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {row.contests.map((c) => (
                          <LotteryChip
                            key={c.contest_id}
                            lotteryName={c.lottery_name}
                            contestName={c.contest_name}
                            color={c.lottery_color}
                            pending={c.status === "pending"}
                          />
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-semibold tabular-nums text-green-700 dark:text-green-400">
                      {Mask.formatCurrency(row.total_prize)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {Mask.formatCurrency(row.client.total_balance)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8"
                        title="Ver apuração do cliente"
                        onClick={() => openClient(row.client.uuid)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            {!loading && rows.length > 0 && (
              <TableFooter>
                <TableRow className="bg-green-50 dark:bg-green-900/10">
                  <TableCell colSpan={2}>
                    {data?.totals.total_clients} {data?.totals.total_clients === 1 ? "cliente" : "clientes"}
                  </TableCell>
                  <TableCell colSpan={3} className="text-right">
                    <span className="mr-3 text-sm text-muted-foreground">Total em prêmios:</span>
                    <span className="text-lg font-bold tabular-nums text-green-700 dark:text-green-400">
                      {Mask.formatCurrency(data?.totals.total_prize ?? 0)}
                    </span>
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </CardContent>
      </Card>

      {data && data.last_page > 1 && (
        <div className="flex items-center justify-end gap-2">
          <span className="text-sm text-muted-foreground">
            Página {data.current_page} de {data.last_page}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={loading || data.current_page <= 1}
            onClick={() => apply(filters, data.current_page - 1)}
          >
            Anterior
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={loading || data.current_page >= data.last_page}
            onClick={() => apply(filters, data.current_page + 1)}
          >
            Próxima
          </Button>
        </div>
      )}
    </Main>
  );
};
